import { signal } from '@preact/signals-react';
import type { WebAgentAssistantActivity } from '@/website/lib/chat-api';
import { shouldRetainAssistantActivityForSurface } from './activity-retention';
import { activityKey, normalizeText } from './conversation-utils';
import type { ConversationCoreTransport } from './types';

export class ActivityLiveDataset {
  readonly activitiesByRuntimeKey = signal(
    new Map<string, WebAgentAssistantActivity>()
  );
  readonly activitiesByConversationId = signal(
    new Map<string, Map<string, WebAgentAssistantActivity>>()
  );
  readonly refreshing = signal(false);
  readonly loaded = signal(false);
  readonly lastError = signal<unknown | null>(null);

  private unsubscribe: (() => void) | null = null;
  private transport: ConversationCoreTransport | null = null;
  private mutationSequence = 0;
  private latestActivitySequenceByRuntimeKey = new Map<string, number>();
  private latestActivityUpdatedAtByRuntimeKey = new Map<string, number>();

  constructor(transport?: ConversationCoreTransport) {
    if (transport) {
      this.configure(transport);
    }
  }

  configure(transport: ConversationCoreTransport) {
    this.stop();
    this.transport = transport;
  }

  private requireTransport() {
    if (!this.transport) {
      throw new Error('ActivityLiveDataset transport is not configured.');
    }
    return this.transport;
  }

  async refresh() {
    const transport = this.requireTransport();
    const refreshStartSequence = this.mutationSequence;
    this.refreshing.value = true;
    this.lastError.value = null;
    try {
      const activities = await transport.listActivities();
      this.replace(activities, refreshStartSequence);
      this.loaded.value = true;
      return activities;
    } catch (error) {
      this.lastError.value = error;
      throw error;
    } finally {
      this.refreshing.value = false;
    }
  }

  start(options: { loadInitial?: boolean } = {}) {
    if (this.unsubscribe) return;
    const transport = this.requireTransport();
    this.unsubscribe = transport.subscribeActivities(
      activity => this.apply(activity),
      error => {
        this.lastError.value = error;
      },
      () => {
        if (options.loadInitial ?? true) {
          void this.refresh().catch(() => {});
        }
      }
    );
  }

  stop() {
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  apply(activity: WebAgentAssistantActivity) {
    const key = activityKey(activity.agentId, activity.sessionId);
    if (!key) return;
    if (
      (this.latestActivityUpdatedAtByRuntimeKey.get(key) ?? -Infinity) >
      activity.updatedAt
    ) {
      return;
    }

    this.mutationSequence += 1;
    this.latestActivitySequenceByRuntimeKey.set(key, this.mutationSequence);
    this.latestActivityUpdatedAtByRuntimeKey.set(key, activity.updatedAt);

    const nextByRuntimeKey = new Map(this.activitiesByRuntimeKey.value);
    const nextByConversationId = this.withoutRuntimeKeyInConversationMaps(key);
    if (shouldRetainAssistantActivityForSurface(activity)) {
      nextByRuntimeKey.set(key, activity);
      const conversationId = normalizeText(activity.conversationId);
      if (conversationId) {
        const conversationActivities = new Map(
          nextByConversationId.get(conversationId) ?? []
        );
        conversationActivities.set(key, activity);
        nextByConversationId.set(conversationId, conversationActivities);
      }
    } else {
      nextByRuntimeKey.delete(key);
    }
    this.activitiesByRuntimeKey.value = nextByRuntimeKey;
    this.activitiesByConversationId.value = nextByConversationId;
  }

  replace(
    activities: WebAgentAssistantActivity[],
    refreshStartSequence = this.mutationSequence
  ) {
    const nextByRuntimeKey = new Map<string, WebAgentAssistantActivity>();
    const nextByConversationId = new Map<
      string,
      Map<string, WebAgentAssistantActivity>
    >();
    for (const activity of activities) {
      const key = activityKey(activity.agentId, activity.sessionId);
      if (!key || !shouldRetainAssistantActivityForSurface(activity)) continue;
      if (
        (this.latestActivityUpdatedAtByRuntimeKey.get(key) ?? -Infinity) >
        activity.updatedAt
      ) {
        continue;
      }
      nextByRuntimeKey.set(key, activity);
    }
    for (const [key, activity] of this.activitiesByRuntimeKey.value) {
      if (
        (this.latestActivitySequenceByRuntimeKey.get(key) ?? 0) >
        refreshStartSequence
      ) {
        nextByRuntimeKey.set(key, activity);
      }
    }
    for (const [key, activity] of nextByRuntimeKey) {
      const conversationId = normalizeText(activity.conversationId);
      if (!conversationId) continue;
      const conversationActivities = new Map(
        nextByConversationId.get(conversationId) ?? []
      );
      conversationActivities.set(key, activity);
      nextByConversationId.set(conversationId, conversationActivities);
    }
    this.activitiesByRuntimeKey.value = nextByRuntimeKey;
    this.activitiesByConversationId.value = nextByConversationId;
  }

  activityFor(
    conversationId: string | null | undefined,
    runtimeActivityKey: string | null | undefined
  ) {
    const normalizedConversationId = normalizeText(conversationId);
    const normalizedRuntimeActivityKey = normalizeText(runtimeActivityKey);
    if (!normalizedConversationId || !normalizedRuntimeActivityKey) return null;

    const activity =
      this.activitiesByRuntimeKey.value.get(normalizedRuntimeActivityKey) ??
      null;
    if (!activity) return null;

    const activityConversationId = normalizeText(activity.conversationId);
    if (
      activityConversationId &&
      activityConversationId !== normalizedConversationId
    ) {
      return null;
    }

    return activity;
  }

  activitiesForConversation(conversationId: string | null | undefined) {
    const normalizedConversationId = normalizeText(conversationId);
    return normalizedConversationId
      ? (this.activitiesByConversationId.value.get(normalizedConversationId) ??
          new Map())
      : new Map<string, WebAgentAssistantActivity>();
  }

  reset() {
    this.activitiesByRuntimeKey.value = new Map();
    this.activitiesByConversationId.value = new Map();
    this.mutationSequence = 0;
    this.latestActivitySequenceByRuntimeKey.clear();
    this.latestActivityUpdatedAtByRuntimeKey.clear();
    this.refreshing.value = false;
    this.loaded.value = false;
    this.lastError.value = null;
  }

  private withoutRuntimeKeyInConversationMaps(runtimeActivityKey: string) {
    const next = new Map<string, Map<string, WebAgentAssistantActivity>>();
    for (const [conversationId, activities] of this.activitiesByConversationId
      .value) {
      if (!activities.has(runtimeActivityKey)) {
        next.set(conversationId, activities);
        continue;
      }
      const conversationActivities = new Map(activities);
      conversationActivities.delete(runtimeActivityKey);
      if (conversationActivities.size > 0) {
        next.set(conversationId, conversationActivities);
      }
    }
    return next;
  }
}
